import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { onValue, ref, update } from 'firebase/database';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import {
  WorkspaceEmptyState,
  WorkspacePageLoader,
  WorkspaceShell,
  WorkspaceStatusChip,
} from '../ui/Workspace';
import { LetterPaper, printStyles } from '../settings/DocumentationSettings';

const StaffLetterReview = () => {
  const { letterId } = useParams();
  const { currentUser } = useAuth();
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [signatureMethod, setSignatureMethod] = useState('name');
  const [signatureImage, setSignatureImage] = useState('');
  const [signaturePlacement, setSignaturePlacement] = useState({ x: 0, y: 0, width: 170 });
  const [acceptanceLocation, setAcceptanceLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dragRef = useRef(null);

  useEffect(() => {
    const unsub = onValue(
      ref(db, 'users'),
      (snapshot) => {
        const users = snapshot.val() || {};
        const found = Object.entries(users).reduce((match, [staffId, user]) => {
          if (match) return match;
          const record = user.letters?.[letterId];
          if (!record) return null;
          return {
            id: letterId,
            staffId,
            staffName:
              record.staffName ||
              `${user.firstName || user.personalDetails?.firstName || ''} ${
                user.lastName || user.personalDetails?.lastName || ''
              }`.trim() ||
              user.displayName ||
              user.email ||
              'Staff member',
            staffEmail: record.staffEmail || user.email || user.personalDetails?.email || '',
            ...record,
          };
        }, null);
        setLetter(found);
        setLoading(false);
      },
      () => {
        setLetter(null);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [letterId]);

  useEffect(() => {
    if (!currentUser || signatureName) return;
    setSignatureName(
      `${currentUser.firstName || currentUser.personalDetails?.firstName || ''} ${
        currentUser.lastName || currentUser.personalDetails?.lastName || ''
      }`.trim() || currentUser.displayName || '',
    );
  }, [currentUser, signatureName]);

  useEffect(() => {
    if (!accepted || letter?.signedAt || acceptanceLocation) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) =>
        setAcceptanceLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        }),
      () => setAcceptanceLocation({ unavailable: true }),
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 5000 },
    );
  }, [accepted, acceptanceLocation, letter?.signedAt]);

  const canReview = useMemo(() => {
    if (!letter || !currentUser) return false;
    return currentUser.role === 'admin' || letter.staffId === currentUser.uid;
  }, [currentUser, letter]);
  const signed = letter?.status === 'signed' || letter?.status === 'verified_signed';

  const signLetter = async () => {
    if (!letter || !currentUser) return;
    if (!accepted) {
      setError('Confirm that you have reviewed and accept the letter terms.');
      return;
    }
    if (signatureMethod === 'name' && !signatureName.trim()) {
      setError('Enter your full name as the digital signature.');
      return;
    }
    if (signatureMethod === 'image' && !signatureImage) {
      setError('Upload a signature image or switch to typed signature.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const signedAt = new Date().toISOString();
      const acceptanceEvidence = {
        acceptedAt: signedAt,
        location: acceptanceLocation || { unavailable: true },
        device: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
        },
      };
      const signedSnapshot = {
        ...letter,
        status: 'signed',
        signedAt,
        acceptedAt: signedAt,
        acceptanceEvidence,
        signedBy: currentUser.uid,
        signatureMethod,
        signatureName: signatureMethod === 'name' ? signatureName.trim() : '',
        signatureImage: signatureMethod === 'image' ? signatureImage : '',
        signaturePlacement,
      };

      await update(ref(db), {
        [`users/${letter.staffId}/letters/${letter.id}/status`]: 'signed',
        [`users/${letter.staffId}/letters/${letter.id}/signedAt`]: signedAt,
        [`users/${letter.staffId}/letters/${letter.id}/acceptedAt`]: signedAt,
        [`users/${letter.staffId}/letters/${letter.id}/signedBy`]: currentUser.uid,
        [`users/${letter.staffId}/letters/${letter.id}/acceptanceEvidence`]:
          acceptanceEvidence,
        [`users/${letter.staffId}/letters/${letter.id}/signatureMethod`]:
          signatureMethod,
        [`users/${letter.staffId}/letters/${letter.id}/signatureName`]:
          signatureMethod === 'name' ? signatureName.trim() : '',
        [`users/${letter.staffId}/letters/${letter.id}/signatureImage`]:
          signatureMethod === 'image' ? signatureImage : '',
        [`users/${letter.staffId}/letters/${letter.id}/signaturePlacement`]:
          signaturePlacement,
        [`users/${letter.staffId}/letters/${letter.id}/signedSnapshot`]: signedSnapshot,
        [`users/${letter.staffId}/appointment/signedLetterId`]: letter.id,
        [`users/${letter.staffId}/appointment/signedAt`]: signedAt,
        [`users/${letter.staffId}/appointment/position`]: letter.position || '',
        [`users/${letter.staffId}/appointment/appointmentDate`]:
          letter.appointmentDate || '',
        [`users/${letter.staffId}/appointment/salary`]: letter.salary || '',
        [`users/${letter.staffId}/appointment/salaryCycle`]:
          letter.salaryCycle || '',
      });
    } catch (signError) {
      console.error('Failed to sign letter:', signError);
      setError('Could not save your signature. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const image = await removeLightSignatureBackground(file);
      setSignatureImage(image);
      setSignatureMethod('image');
    } catch (uploadError) {
      console.error('Failed to prepare signature image:', uploadError);
      setError('Could not read this signature image. Try another PNG or JPG file.');
    }
  };

  const startSignatureDrag = (event) => {
    if (signed || !signatureImage) return;
    event.preventDefault();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      original: signaturePlacement,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveSignature = (event) => {
    if (!dragRef.current) return;
    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    setSignaturePlacement({
      ...dragRef.current.original,
      x: Math.max(-80, Math.min(80, dragRef.current.original.x + deltaX)),
      y: Math.max(-28, Math.min(28, dragRef.current.original.y + deltaY)),
    });
  };

  const stopSignatureDrag = () => {
    dragRef.current = null;
  };

  if (loading) return <WorkspacePageLoader />;
  if (!letter) {
    return (
      <WorkspaceShell title="Letter Review" description="Review and sign staff documents.">
        <WorkspaceEmptyState
          title="Letter not found"
          message="This staff letter could not be found or is no longer available."
        />
      </WorkspaceShell>
    );
  }
  if (!canReview) return <Navigate to="/dashboard" replace />;

  return (
    <WorkspaceShell
      title="Appointment Letter Review"
      description="Review the official appointment letter and sign acceptance on the platform."
      actions={<WorkspaceStatusChip status={letter.status} label={formatStatus(letter.status)} />}
    >
      <style>{printStyles}</style>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <LetterPaper letter={letter} mode="registry" />

        <aside className="h-fit rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-stone-950">Staff acceptance</p>
          <p className="mt-1 text-sm leading-5 text-stone-600">
            Signing confirms that you reviewed and accepted the appointment terms.
          </p>

          <label className="mt-4 flex gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={accepted || signed}
              disabled={signed}
              onChange={(event) => setAccepted(event.target.checked)}
              className="mt-1 rounded border-stone-300 text-[#d61f74] focus:ring-[#d61f74]"
            />
            I have reviewed this letter and accept the appointment terms.
          </label>

          <div className="mt-4 rounded-xl border border-stone-200 bg-stone-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
              Signature
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={signed}
                onClick={() => setSignatureMethod('name')}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                  signatureMethod === 'name'
                    ? 'border-[#d61f74] bg-[#fdf2f8] text-[#a40f55]'
                    : 'border-stone-200 bg-white text-stone-700'
                }`}
              >
                Type name
              </button>
              <button
                type="button"
                disabled={signed}
                onClick={() => setSignatureMethod('image')}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                  signatureMethod === 'image'
                    ? 'border-[#d61f74] bg-[#fdf2f8] text-[#a40f55]'
                    : 'border-stone-200 bg-white text-stone-700'
                }`}
              >
                Upload image
              </button>
            </div>

            {signatureMethod === 'name' ? (
              <label className="mt-3 block">
                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Full name as digital signature
                </span>
                <input
                  value={letter.signatureName || signatureName}
                  disabled={signed}
                  onChange={(event) => setSignatureName(event.target.value)}
                  className="mt-1 block w-full rounded-xl border-stone-300 text-sm focus:border-[#d61f74] focus:ring-[#d61f74]"
                />
              </label>
            ) : (
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Signature image
                  </span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={signed}
                    onChange={handleSignatureUpload}
                    className="mt-1 block w-full text-sm text-stone-600 file:mr-3 file:rounded-lg file:border-0 file:bg-stone-900 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white"
                  />
                </label>
                <div
                  onPointerDown={startSignatureDrag}
                  onPointerMove={moveSignature}
                  onPointerUp={stopSignatureDrag}
                  onPointerCancel={stopSignatureDrag}
                  className="relative h-24 overflow-hidden rounded-xl border border-dashed border-stone-300 bg-white"
                >
                  <div className="absolute inset-x-4 bottom-5 border-b border-stone-700" />
                  {signatureImage ? (
                    <img
                      src={signatureImage}
                      alt="Signature preview"
                      className="absolute bottom-5 left-8 cursor-move object-contain"
                      style={{
                        width: `${signaturePlacement.width}px`,
                        transform: `translate(${signaturePlacement.x}px, ${signaturePlacement.y}px)`,
                        filter: 'grayscale(1) contrast(1.45)',
                        mixBlendMode: 'multiply',
                      }}
                      draggable={false}
                    />
                  ) : (
                    <p className="absolute inset-0 flex items-center justify-center text-xs text-stone-400">
                      Upload a signature image
                    </p>
                  )}
                </div>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Signature size
                  </span>
                  <input
                    type="range"
                    min="110"
                    max="240"
                    value={signaturePlacement.width}
                    disabled={signed}
                    onChange={(event) =>
                      setSignaturePlacement((current) => ({
                        ...current,
                        width: Number(event.target.value),
                      }))
                    }
                    className="mt-1 w-full accent-[#d61f74]"
                  />
                </label>
              </div>
            )}
          </div>

          <div className="mt-3 rounded-xl bg-stone-50 px-3 py-2 text-xs leading-5 text-stone-600">
            Evidence saved on acceptance: timestamp, device, browser, timezone,
            and location when permission is granted.
          </div>

          {letter.signedAt ? (
            <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
              Signed on {new Date(letter.signedAt).toLocaleString()}
            </p>
          ) : null}
          {error ? <p className="mt-3 text-sm font-semibold text-red-600">{error}</p> : null}

          <div className="mt-4 flex flex-col gap-2">
            <Button onClick={signLetter} disabled={saving || signed}>
              {signed ? 'Signed' : saving ? 'Signing...' : 'Accept and Sign'}
            </Button>
            <Button type="button" variant="outline" onClick={() => window.print()}>
              Print copy
            </Button>
          </div>
        </aside>
      </div>
    </WorkspaceShell>
  );
};

const formatStatus = (status = 'draft') =>
  status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const removeLightSignatureBackground = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const image = new Image();
      image.onerror = reject;
      image.onload = () => {
        const maxWidth = 640;
        const scale = Math.min(1, maxWidth / image.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const frame = context.getImageData(0, 0, canvas.width, canvas.height);
        for (let index = 0; index < frame.data.length; index += 4) {
          const red = frame.data[index];
          const green = frame.data[index + 1];
          const blue = frame.data[index + 2];
          const brightness = (red + green + blue) / 3;
          if (brightness > 228 && Math.max(red, green, blue) - Math.min(red, green, blue) < 24) {
            frame.data[index + 3] = 0;
          } else {
            const ink = brightness < 120 ? 20 : 55;
            frame.data[index] = ink;
            frame.data[index + 1] = ink;
            frame.data[index + 2] = ink;
          }
        }
        context.putImageData(frame, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

export default StaffLetterReview;
